/**
 * App-wide i18n (i18next, vi + en). Conventions, glossary and "how to add a language / namespace"
 * live in docs/i18n.md - read it before touching a string.
 *
 * - React component: `const { t } = useTranslation('<ns>')` from 'react-i18next'.
 * - Non-React .ts:   `import { i18n } from '<relative path>/i18n/index.ts'` and call `i18n.t('ns:key')` inside functions
 *   (no standalone `t` is exported: it would lose `this` and its typed overloads; node --test cannot resolve `@/`).
 * - Never call t() at module top level: it freezes the language at import time.
 *
 * Importing this module initialises i18next synchronously (resources are bundled, boards can be
 * offline) and works without a window, so node tests run in the default language (vi).
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { readJson, writeJson } from '../api/storage.ts';
import { DEFAULT_LANGUAGE, isLanguage, type Language } from './languages.ts';
import { NAMESPACES, resources } from './resources.ts';

/** Device-level preference: a shared board keeps the last choice. */
export const LANGUAGE_STORAGE_KEY = 'izteach.language';

// Stored value, else vi. navigator.language is ignored on purpose: panels often ship with an
// English OS locale while the school is Vietnamese.
function detectLanguage(): Language {
  const stored = readJson<unknown>(LANGUAGE_STORAGE_KEY);
  return isLanguage(stored) ? stored : DEFAULT_LANGUAGE;
}

function syncDocumentLang(language: string): void {
  if (typeof document !== 'undefined') document.documentElement.lang = language;
}

void i18n.use(initReactI18next).init({
  resources,
  lng: detectLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: ['vi', 'en'],
  ns: [...NAMESPACES],
  defaultNS: 'common',
  returnNull: false,
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
  initAsync: false,
});

syncDocumentLang(i18n.language);

export { i18n };
export default i18n;

export function getLanguage(): Language {
  return isLanguage(i18n.language) ? i18n.language : DEFAULT_LANGUAGE;
}

export function setLanguage(language: Language): void {
  if (language === getLanguage()) return;
  void i18n.changeLanguage(language);
  writeJson(LANGUAGE_STORAGE_KEY, language);
  syncDocumentLang(language);
}
